import { motion, useInView, useAnimate, stagger } from 'framer-motion';
import { useEffect } from 'react';
import { useInViewScroll, useSanitizedTranslation, StackedContent } from 'ui';

type LogoCardVariant = 'image' | 'text';

export interface LogoCardGridProps {
  stackedContent: any;
  logoCards: LogoCardProps[];
}

export interface LogoCardProps {
  variant?: LogoCardVariant;
  image?: { src: string; alt: string; fullWidth?: boolean };
  title: string;
  body: string;
}

const LogoCard = ({ title, image, body, variant }: LogoCardProps) => {
  const { t } = useSanitizedTranslation();

  const [ref, animate] = useAnimate();
  const inView = useInView(ref, { once: true, margin: '-30% 0% -30% 0%' });

  useEffect(() => {
    if (inView) {
      animate(
        'div, p, h1, h2, h3, h4, .img, .content, .btn',
        {
          y: [30, 0],
          opacity: [0, 1],
        },
        {
          ease: [0, 1, 1, 1],
          duration: 1.2,
          delay: stagger(0.33),
          deltaRest: 0.000001,
          deltaSpeed: 0.000001,
        },
      );
    }
  }, [inView]);

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0 }}
      whileInView={{ opacity: 1 }}
      viewport={{ once: true, margin: '-30% 0% -30% 0%' }}
      className={`col-span-1 mx-auto flex max-w-[425px] flex-col items-center rounded-3xl bg-xumoDarkPlum px-10 lg:mx-0 ${
        variant === 'image' ? 'gap-4 py-16' : 'gap-8 py-10'
      }`}
      style={{ boxShadow: '0 0 60px 0 #5B2E4F' }}
    >
      {variant === 'text' ? (
        <div className="flex items-center  xl:min-h-[160px]">
          <p className="type-body-light text-center text-white" dangerouslySetInnerHTML={{ __html: t(title) }} />
        </div>
      ) : (
        <div className="relative flex h-[100px] w-[244px] items-center justify-center">
          <img src={image?.src} alt={image?.alt} className={`h-auto object-contain ${image?.fullWidth ? 'w-full' : 'max-h-[75px] w-auto'}`} />
        </div>
      )}

      <p data-i18n={t(body)} dangerouslySetInnerHTML={{ __html: t(body) }} className="text-center text-white" />
    </motion.div>
  );
};

const LogoCardGrid = ({ variant, stackedContent, logoCards }: LogoCardGridProps) => {
  return (
    <motion.section className="relative z-10 flex w-full flex-col justify-center bg-xumoDarkPlum py-16 lg:py-32" data-testid={'text-section'}>
      <div className="wrapper w-[80%] md:w-full">
        <StackedContent fullalways fullheight mobileCenterLg center content={stackedContent} />
      </div>
      <div className="wrapper mt-12 grid w-full grid-cols-1 gap-8 md:mt-24 lg:grid-cols-2 xl:grid-cols-4">
        {logoCards.map((card, index) => (
          <LogoCard key={'logoCard' + index} title={card.title} image={card.image} body={card.body} variant={variant} />
        ))}
      </div>
    </motion.section>
  );
};

export default LogoCardGrid;
